/**
 * The artifact card's surface, transcribed from the approved V3 mockup
 * (`prototype/coach-renewal/AI Coach webview overhaul/Coach v3.dc.html`).
 *
 * Inline styles rather than Tailwind classes, matching SetGrid / TrendBadge /
 * MachineCard — and required here anyway, since several values are chosen at
 * runtime and Tailwind's JIT cannot scan runtime strings.
 */
import type { CSSProperties } from 'react'

import { TOKENS } from '../../ui/dashboard-primitives'

/**
 * The card itself — in V3 an **inset well inside the coach bubble**, not a
 * free-floating card. Shared so the exercise card — and, from Spec I, the video
 * card — which bleed their media to the edge and cannot use <ArtifactCard>,
 * still sit on it.
 *
 * Three absences are load-bearing, not omissions:
 *  - **No `maxWidth`.** The bubble's `max-width: 85%` bounds the card now.
 *    V2's `30rem` cap would fight it and leave a ragged right edge inside a
 *    surface that already has one.
 *  - **No `boxShadow`.** The bubble carries the elevation; a shadow inside a
 *    shadowed container reads as a second, non-existent layer.
 *  - **A darker wash than the bubble**, so the well recedes rather than
 *    competing with the prose it sits beneath.
 */
export const ARTIFACT_SURFACE: CSSProperties = {
  borderRadius: 14,
  padding: '14px 16px',
  background: 'rgba(26, 20, 19, 0.45)',
  border: `1px solid ${TOKENS.panelBorderDark}`,
}

/**
 * A name on a card that the user or the catalog chose: a template, an
 * exercise, a clip (#310). A word in it wider than the card used to run past
 * it and be clipped. It is `anywhere` rather than `break-word` because these
 * names are grid and flex items (`1fr auto`, the exercise card's `96px 1fr`)
 * whose track width comes from min-content, which only `anywhere` lowers.
 *
 * Put on the names, never on `ARTIFACT_SURFACE`: it is inherited, and on the
 * whole surface it squeezed the muscles card's auto-sized legend to one letter
 * per line at 390px ("Chest (prime mover)"). A name that already fits is
 * unaffected.
 */
export const ARTIFACT_NAME: CSSProperties = {
  overflowWrap: 'anywhere',
}

export const ARTIFACT_EYEBROW: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 9,
  letterSpacing: '0.24em',
  textTransform: 'uppercase',
  color: TOKENS.amber,
}

/**
 * The label of a card's one action ("Open →", "Session →", "Knowledge Base →").
 *
 * 0.6875rem, not 9px (SH-4 of the 2026-10-15 web design review): this is the
 * home screen's main call to action, and in rem it follows the person's text
 * size. The label stays small on purpose, it is the cue and not the target:
 * the target is the whole card (`<ArtifactAction stretch>`) or, where the card
 * holds a control of its own, a 44px box grown around the label.
 */
export const ARTIFACT_ACTION: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6875rem',
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  color: TOKENS.amber,
}

/**
 * What a card that is itself the link does (SH-4 of the 2026-10-15 web design
 * review): its `::after` covers the card, lights up a little under a pointer
 * (`hover`, so only where there is one) and more under a press, and draws the
 * focus ring inside the card. The card needs `position: relative`.
 *
 * Three choices, each from a measurement in a browser:
 *
 * - **A wash, not a scale.** A press that scaled the card to 0.98 moved its edge
 *   out from under the pointer, and a press and release within 3px of that edge
 *   lost its click. A wash moves nothing.
 * - **The ring is inside the card** (`-outline-offset-4`). A card anchored to a
 *   paragraph sits in a wrapper with `overflow: hidden` whose box is the card's
 *   (`AnchoredArtifacts`), so a ring drawn outside it is cut off. Inside, it is
 *   also drawn over the card's own dark well, where amber is above 3:1, and not
 *   over the page gradient's tan peak, where it was 2.8:1.
 * - **`outline-none` on the link:** the ring is the `::after`'s.
 *
 * A class and not a style because an inline style cannot say `:hover`,
 * `:active` or `::after`. The card's own background stays inline.
 */
export const ARTIFACT_CARD_LINK =
  "outline-none after:absolute after:inset-0 after:rounded-[14px] after:content-[''] after:transition-colors after:duration-150 after:ease-out hover:after:bg-(--text-cream-hairline) active:after:bg-(--text-cream-border) focus-visible:after:outline-2 focus-visible:after:-outline-offset-4 focus-visible:after:outline-(--accent-amber)"

export const ARTIFACT_META: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 9,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  color: TOKENS.creamDim,
}

export const ARTIFACT_TITLE: CSSProperties = {
  ...ARTIFACT_NAME,
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 18,
  letterSpacing: '-0.02em',
  color: TOKENS.cream,
}

const CHIP_BASE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '3px 9px',
  borderRadius: 9999,
  fontFamily: 'var(--font-mono)',
  fontSize: 9,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
}

export const ARTIFACT_CHIP_AMBER: CSSProperties = {
  ...CHIP_BASE,
  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
  background: 'rgba(232, 197, 160, 0.12)',
  color: TOKENS.amber,
  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
  border: '1px solid rgba(232, 197, 160, 0.28)',
}

export const ARTIFACT_CHIP_NEUTRAL: CSSProperties = {
  ...CHIP_BASE,
  background: TOKENS.creamHairline,
  color: TOKENS.creamSoft,
  border: '1px solid ' + TOKENS.creamBorder,
}

/**
 * V3 lightens the warn chip for the darker well. Raw `#c66e4d` was legible on
 * V2's `rgba(245,237,225,0.05)` card; on `rgba(26,20,19,0.45)` it is a dark
 * orange on a dark brown, at the 9px this chip uses. `color-mix` lifts it
 * toward cream while keeping the hue, and the fill goes 0.16 → 0.2 to hold the
 * chip's edge against the well. `TOKENS.terracotta` is the mix's first stop,
 * not the final colour — do not "simplify" this back to the token.
 */
export const ARTIFACT_CHIP_WARN: CSSProperties = {
  ...CHIP_BASE,
  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
  background: 'rgba(198, 110, 77, 0.2)',
  color: `color-mix(in oklab, ${TOKENS.terracotta} 42%, ${TOKENS.cream} 58%)`,
  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
  border: '1px solid rgba(198, 110, 77, 0.32)',
}
